import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Link2, List, ListOrdered, Loader2, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { api, type LandingSectionEditPatch } from "@/lib/api";
import { Button } from "@/components/ui/button";

type EditorTab = "diff" | "wysiwyg" | "html";

type Props = {
  open: boolean;
  pageId: string;
  sectionId: string | null;
  sourceRel?: string;
  pendingEdits: LandingSectionEditPatch[];
  onClose: () => void;
  onSaved: (note?: string) => void;
  onClearPending: () => void;
};

/** Inline <span class="…"> – StarterKit je wyrzuca i gubi style landingu (np. sj-accent). */
const PreserveSpan = Node.create({
  name: "preserveSpan",
  group: "inline",
  inline: true,
  content: "inline*",
  parseHTML() {
    return [{ tag: "span" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (el) => el.getAttribute("class"),
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
    };
  },
});

const PreserveClassAttrs = Extension.create({
  name: "preserveClassAttrs",
  addGlobalAttributes() {
    return [
      {
        types: [
          "paragraph",
          "heading",
          "bulletList",
          "orderedList",
          "listItem",
          "blockquote",
          "link",
        ],
        attributes: {
          class: {
            default: null,
            parseHTML: (el) => el.getAttribute("class"),
            renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
          },
        },
      },
    ];
  },
});

const SECTION_OPEN_RE = /<(section|header)\b([^>]*)>/gi;
const ID_ATTR_RE = /\bid\s*=\s*(?:["']([^"']+)["']|([^\s>]+))/i;

function listSectionIds(html: string): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  const re = new RegExp(SECTION_OPEN_RE.source, "gi");
  while ((m = re.exec(html || ""))) {
    const idM = ID_ATTR_RE.exec(m[2] || "");
    const id = (idM?.[1] || idM?.[2] || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function extractSectionHtml(html: string, sectionId: string): string | null {
  const sid = (sectionId || "").trim();
  if (!sid) return null;
  const text = html || "";
  let target: RegExpExecArray | null = null;
  const openRe = new RegExp(SECTION_OPEN_RE.source, "gi");
  let m: RegExpExecArray | null;
  while ((m = openRe.exec(text))) {
    const idM = ID_ATTR_RE.exec(m[2] || "");
    const found = (idM?.[1] || idM?.[2] || "").trim();
    if (found === sid) {
      target = m;
      break;
    }
  }
  if (!target) return null;
  const tag = (target[1] || "section").toLowerCase();
  let pos = target.index + target[0].length;
  let depth = 1;
  const nestedOpen = new RegExp(`<${tag}\\b`, "gi");
  const nestedClose = new RegExp(`</${tag}\\s*>`, "gi");
  while (pos < text.length && depth > 0) {
    nestedOpen.lastIndex = pos;
    nestedClose.lastIndex = pos;
    const nextOpen = nestedOpen.exec(text);
    const nextClose = nestedClose.exec(text);
    if (!nextClose) break;
    if (nextOpen && nextOpen.index < nextClose.index) {
      depth += 1;
      pos = nextOpen.index + nextOpen[0].length;
      continue;
    }
    depth -= 1;
    pos = nextClose.index + nextClose[0].length;
    if (depth === 0) {
      return text.slice(target.index, pos);
    }
  }
  return null;
}

function innerHtmlOfSection(sectionHtml: string): string {
  const m = sectionHtml.match(/^<([a-z0-9:-]+)\b[^>]*>([\s\S]*)<\/\1\s*>$/i);
  return m ? m[2] : sectionHtml;
}

function rebuildSection(sectionBefore: string, inner: string): string | null {
  const m = sectionBefore.match(/^<([a-z0-9:-]+)\b([^>]*)>/i);
  if (!m) return null;
  const tag = m[1];
  const attrs = m[2] || "";
  return `<${tag}${attrs}>${inner}</${tag}>`;
}

export default function LandingHtmlEditorModal({
  open,
  pageId,
  sectionId,
  sourceRel,
  pendingEdits,
  onClose,
  onSaved,
  onClearPending,
}: Props) {
  const [tab, setTab] = useState<EditorTab>("html");
  const [fullHtml, setFullHtml] = useState("");
  const [sectionBefore, setSectionBefore] = useState("");
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [diffHtml, setDiffHtml] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadWarning, setLoadWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const sectionIds = useMemo(() => listSectionIds(fullHtml), [fullHtml]);
  const hasPending = pendingEdits.length > 0;
  const effectiveSectionId = activeSectionId || sectionId;
  const canWysiwyg = Boolean(effectiveSectionId && sectionBefore);

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions: [
        StarterKit,
        Link.configure({ openOnClick: false }),
        Placeholder.configure({ placeholder: "Treść sekcji…" }),
        PreserveSpan,
        PreserveClassAttrs,
      ],
      content: "",
      editorProps: {
        attributes: {
          class:
            "prose prose-sm max-w-none min-h-[50vh] px-4 py-3 focus:outline-none",
        },
      },
    },
    [open],
  );

  const applySectionToEditor = (docHtml: string, sid: string | null) => {
    if (!sid) {
      setSectionBefore("");
      return false;
    }
    const extracted = extractSectionHtml(docHtml, sid);
    if (!extracted) {
      setSectionBefore("");
      return false;
    }
    setSectionBefore(extracted);
    if (editor && !editor.isDestroyed) {
      try {
        editor.commands.setContent(innerHtmlOfSection(extracted) || "<p></p>");
      } catch {
        // TipTap może nie być gotowy; efekt poniżej dociągnie treść.
      }
    }
    return true;
  };

  useEffect(() => {
    if (!open || !pageId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setLoadWarning(null);
    setDiffHtml("");
    setSectionBefore("");
    setActiveSectionId(sectionId);

    (async () => {
      try {
        const preferPending = hasPending && Boolean(sectionId);
        if (preferPending && sectionId) {
          try {
            const [patched, diff] = await Promise.all([
              api.landingPreviewSourceWithPatches({
                page_id: pageId,
                section_id: sectionId,
                patches: pendingEdits,
              }),
              api.landingPreviewDiffHtml({
                page_id: pageId,
                section_id: sectionId,
                patches: pendingEdits,
              }),
            ]);
            if (cancelled) return;
            const html = patched.html || "";
            setFullHtml(html);
            setDiffHtml(diff);
            setActiveSectionId(sectionId);
            const before =
              patched.section_html_before ||
              extractSectionHtml(html, sectionId) ||
              "";
            setSectionBefore(before);
            if (editor && !editor.isDestroyed) {
              try {
                editor.commands.setContent(
                  innerHtmlOfSection(patched.section_html || before || "") ||
                    "<p></p>",
                );
              } catch {
                /* sync effect */
              }
            }
            setTab("diff");
            return;
          } catch (patchErr) {
            if (!cancelled) {
              setLoadWarning(
                patchErr instanceof Error
                  ? `Pending nie zastosowano (${patchErr.message}). Pokazuję plik z dysku.`
                  : "Pending nie zastosowano. Pokazuję plik z dysku.",
              );
            }
          }
        }

        const src = await api.landingPreviewSource(pageId);
        if (cancelled) return;
        const html = src.html || "";
        setFullHtml(html);

        const ids = listSectionIds(html);
        const sid =
          (sectionId && ids.includes(sectionId) ? sectionId : null) ||
          ids[0] ||
          null;
        setActiveSectionId(sid);

        if (sid && applySectionToEditor(html, sid)) {
          setTab(preferPending ? "diff" : "wysiwyg");
        } else {
          setTab("html");
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Nie udało się wczytać HTML");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pageId, sectionId, hasPending, pendingEdits]);

  // Gdy TipTap wstanie albo zmieni się sekcja: wgraj treść do edytora.
  useEffect(() => {
    if (!open || !editor || editor.isDestroyed || !sectionBefore) return;
    try {
      editor.commands.setContent(innerHtmlOfSection(sectionBefore) || "<p></p>");
    } catch {
      /* ignore transient TipTap race */
    }
  }, [open, editor, sectionBefore]);

  // Gdy user zmieni sekcję w selectcie: wytnij z fullHtml.
  useEffect(() => {
    if (!open || loading || !fullHtml || !activeSectionId) return;
    const extracted = extractSectionHtml(fullHtml, activeSectionId);
    if (extracted && extracted !== sectionBefore) {
      setSectionBefore(extracted);
    }
    // sectionBefore celowo nie w deps (unikamy pętli)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, fullHtml, activeSectionId]);

  const saveM = useMutation({
    mutationFn: async () => {
      const mergeWysiwygInto = (docHtml: string, sectionHtml: string) => {
        if (!editor || editor.isDestroyed || !sectionHtml) return docHtml;
        const rebuilt = rebuildSection(sectionHtml, editor.getHTML());
        if (!rebuilt) throw new Error("Nie udało się złożyć sekcji");
        if (!docHtml.includes(sectionHtml) || docHtml.split(sectionHtml).length !== 2) {
          throw new Error("Sekcja nie jest unikalna w dokumencie");
        }
        return docHtml.replace(sectionHtml, rebuilt);
      };

      const sid = effectiveSectionId;

      if (hasPending && sid) {
        await api.landingSectionEditsApply({
          page_id: pageId,
          section_id: sid,
          patches: pendingEdits.map((p) => ({
            old_text: p.old_text,
            new_text: p.new_text,
            note: p.note || "",
          })),
        });
        onClearPending();

        if (tab === "html") {
          await api.landingPreviewSourceSave({
            page_id: pageId,
            html: fullHtml,
            note: "ręczna edycja HTML",
          });
          return { note_pl: "Zastosowano patchy i zapisano HTML." };
        }
        if (tab === "wysiwyg" && sectionBefore) {
          const src = await api.landingPreviewSource(pageId);
          const currentSec =
            extractSectionHtml(src.html || "", sid) || sectionBefore;
          const merged = mergeWysiwygInto(src.html || "", currentSec);
          if (merged !== (src.html || "")) {
            await api.landingPreviewSourceSave({
              page_id: pageId,
              html: merged,
              note: "edycja WYSIWYG sekcji",
            });
          }
          return { note_pl: "Zastosowano patchy i zapisano WYSIWYG." };
        }
        return { note_pl: "Zastosowano zmiany z chatu." };
      }

      if (tab === "wysiwyg") {
        if (!sectionBefore) {
          throw new Error("Brak sekcji do zapisu WYSIWYG");
        }
        const merged = mergeWysiwygInto(fullHtml, sectionBefore);
        return api.landingPreviewSourceSave({
          page_id: pageId,
          html: merged,
          note: "edycja WYSIWYG sekcji",
        });
      }

      return api.landingPreviewSourceSave({
        page_id: pageId,
        html: fullHtml,
        note: "ręczna edycja HTML",
      });
    },
    onSuccess: (res) => {
      onSaved(res.note_pl || "Zapisano HTML.");
      onClose();
    },
  });

  useEffect(() => {
    if (open) saveM.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Dociągnij diff, gdy user wejdzie w zakładkę bez wcześniej załadowanego srcDoc.
  useEffect(() => {
    if (!open || tab !== "diff" || !hasPending || !effectiveSectionId) return;
    if (diffHtml) return;
    let cancelled = false;
    api
      .landingPreviewDiffHtml({
        page_id: pageId,
        section_id: effectiveSectionId,
        patches: pendingEdits,
      })
      .then((html) => {
        if (!cancelled) setDiffHtml(html);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setLoadWarning(
            err?.message
              ? `Podgląd zmian niedostępny: ${err.message}`
              : "Podgląd zmian niedostępny.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    tab,
    hasPending,
    effectiveSectionId,
    diffHtml,
    pageId,
    pendingEdits,
  ]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !saveM.isPending) {
        ev.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saveM.isPending]);

  if (!open) return null;

  const canSave =
    !saveM.isPending && !loading && !loadError && Boolean(fullHtml.trim());

  const tabs: { id: EditorTab; label: string }[] = [
    { id: "diff", label: "Podgląd zmian" },
    { id: "wysiwyg", label: "WYSIWYG" },
    { id: "html", label: "HTML" },
  ];

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex h-dvh w-screen flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Edycja HTML landingu"
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Edycja HTML</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {sourceRel || pageId}
            {effectiveSectionId ? ` · #${effectiveSectionId}` : ""}
            {hasPending ? " · z pending chat" : ""}
            {loading ? " · wczytuję…" : ""}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 shrink-0 p-0"
          aria-label="Zamknij edycję"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2">
        <div className="flex flex-wrap items-center gap-1">
          {tabs.map((t) => (
            <Button
              key={t.id}
              type="button"
              size="sm"
              variant={tab === t.id ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              disabled={loading}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </Button>
          ))}
        </div>
        {sectionIds.length > 0 ? (
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
            Sekcja
            <select
              className="h-7 max-w-[10rem] rounded-md border border-input bg-white px-2 text-xs text-foreground"
              value={effectiveSectionId || ""}
              disabled={loading}
              onChange={(e) => {
                const next = e.target.value || null;
                setActiveSectionId(next);
                if (next && fullHtml) {
                  applySectionToEditor(fullHtml, next);
                  if (tab === "html") setTab("wysiwyg");
                }
              }}
            >
              {sectionIds.map((id) => (
                <option key={id} value={id}>
                  #{id}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {(loadWarning || loadError || saveM.isError) && (
        <div className="shrink-0 space-y-1 border-b bg-slate-50 px-4 py-2">
          {loadWarning ? (
            <p className="text-[11px] text-amber-700">{loadWarning}</p>
          ) : null}
          {loadError ? (
            <p className="text-[11px] text-rose-700">{loadError}</p>
          ) : null}
          {saveM.isError ? (
            <p className="text-[11px] text-rose-700">
              {(saveM.error as Error)?.message || "Zapis nieudany"}
            </p>
          ) : null}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {loading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Wczytuję…
          </div>
        ) : null}

        {!loading && tab === "diff" ? (
          hasPending && diffHtml ? (
            <iframe
              title="Podgląd zmian"
              className="min-h-0 w-full flex-1 border-0 bg-white"
              srcDoc={diffHtml}
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="flex flex-1 flex-col items-start justify-center gap-2 p-6 text-sm text-muted-foreground">
              <p>
                {hasPending
                  ? "Buduję podgląd zmian…"
                  : "Brak pending z chatu. Najpierw zaproponuj poprawkę w czacie landingu, albo edytuj w WYSIWYG / HTML."}
              </p>
              {!hasPending ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setTab(canWysiwyg ? "wysiwyg" : "html")}
                >
                  Przejdź do {canWysiwyg ? "WYSIWYG" : "HTML"}
                </Button>
              ) : null}
            </div>
          )
        ) : null}

        {!loading && tab === "wysiwyg" ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {!canWysiwyg ? (
              <p className="p-4 text-sm text-muted-foreground">
                Nie znaleziono sekcji w HTML. Wybierz sekcję z listy powyżej albo edytuj zakładkę HTML.
              </p>
            ) : (
              <>
                <div className="flex shrink-0 flex-wrap gap-1 border-b px-3 py-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                  >
                    <Bold className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                  >
                    <Italic className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  >
                    <List className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() =>
                      editor?.chain().focus().toggleOrderedList().run()
                    }
                  >
                    <ListOrdered className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => {
                      const prev = window.prompt("URL linku");
                      if (!prev) return;
                      editor?.chain().focus().setLink({ href: prev }).run();
                    }}
                  >
                    <Link2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
                  <EditorContent editor={editor} />
                </div>
              </>
            )}
          </div>
        ) : null}

        {!loading && tab === "html" ? (
          <textarea
            className="min-h-0 w-full flex-1 resize-none border-0 bg-slate-50 p-4 font-mono text-xs leading-relaxed outline-none"
            value={fullHtml}
            onChange={(e) => setFullHtml(e.target.value)}
            spellCheck={false}
          />
        ) : null}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saveM.isPending}
          onClick={onClose}
        >
          Anuluj
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canSave}
          onClick={() => saveM.mutate()}
        >
          {saveM.isPending ? "Zapisuję…" : "Zapisz"}
        </Button>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
